const opKey = (o, n) => `${o.fiche_code}${o.is_option ? " (option)" : ""}${n > 1 ? ` #${n}` : ""}`;

const fmtValue = (f, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (f?.type === "boolean") return v ? "Oui" : "Non";
  if (f?.type === "select") return f.options?.find((o) => String(o.value) === String(v))?.label || String(v);
  if (typeof v === "number" || (f?.type === "number" && !Number.isNaN(Number(v)))) return `${Number(v).toLocaleString("fr-FR")}${f?.unit ? ` ${f.unit}` : ""}`;
  return String(v);
};

const keyedOps = (sim) => {
  const seen = {};
  return (sim.operations || []).map((o) => {
    const base = opKey(o, 1);
    seen[base] = (seen[base] || 0) + 1;
    return { key: opKey(o, seen[base]), op: o };
  });
};

const fr = (v) => Number(v).toLocaleString("fr-FR");

const pacRows = (f, ops) => {
  const lists = ops.map((o) => (o?.inputs?.[f.key] || []).filter((x) => x && (x.puissance || x.perf)));
  const perf = (o, x) => (x.perf === "" || x.perf == null ? "—" : o?.inputs?.tranche === "gt400" ? `COP ${fr(x.perf)}` : `${(f.perf_label?.le400 || "ηs").split(" ")[0]} ${fr(x.perf)} %`);
  const rows = [{ key: "pacs-n", label: "Nombre de PAC", values: ops.map((o, i) => (o ? String(lists[i].length) : "—")), raw: lists.map((l) => l.length) }];
  const max = Math.max(0, ...lists.map((l) => l.length));
  for (let n = 0; n < max; n += 1) {
    rows.push({ key: `pac-${n}-p`, label: `PAC n°${n + 1} — puissance`, values: lists.map((l) => (l[n] ? `${fr(l[n].puissance || 0)} kW` : "—")), raw: lists.map((l) => l[n]?.puissance ?? null) });
    const pv = ops.map((o, i) => (lists[i][n] ? perf(o, lists[i][n]) : "—"));
    rows.push({ key: `pac-${n}-perf`, label: `PAC n°${n + 1} — performance`, values: pv, raw: pv });
  }
  const tot = lists.map((l) => l.reduce((s, x) => s + (Number(x.puissance) || 0), 0));
  rows.push({ key: "pacs-total", label: "Puissance totale installée (P)", values: ops.map((o, i) => (o ? `${fr(tot[i])} kW` : "—")), raw: tot });
  return rows;
};

const visible = (f, op) => !!op && Object.entries(f.show_if || {}).every(([k, v]) => String(op.inputs?.[k]) === String(v));

export const inputsMatrix = (sims, fichesByCode) => {
  const groups = [];
  sims.forEach((s) => keyedOps(s).forEach(({ key, op }) => {
    if (!groups.some((g) => g.key === key)) groups.push({ key, code: op.fiche_code });
  }));
  return groups.map((g) => {
    const fiche = fichesByCode[g.code];
    const perSim = sims.map((s) => keyedOps(s).find((k) => k.key === g.key)?.op || null);
    const stepsOf = (op) => (op?.steps?.length ? op.steps : op ? [op.inputs || {}] : []);
    const maxSteps = Math.max(1, ...perSim.map((op) => stepsOf(op).length));
    const fieldRows = (ops, prefix) => (fiche?.fields || []).flatMap((f) => {
      if (f.type === "pac_list") return pacRows(f, ops).map((r) => ({ ...r, key: prefix + r.key, label: prefix + r.label }));
      const raw = ops.map((op) => (visible(f, op) ? op.inputs?.[f.key] : undefined));
      const values = ops.map((op, i) => (!op ? "—" : visible(f, op) ? fmtValue(f, raw[i]) : "Non applicable"));
      return [{ key: prefix + f.key, label: prefix + f.label, values, raw }];
    });
    const rows = Array.from({ length: maxSteps }, (_, n) => {
      const ops = perSim.map((op) => { const st = stepsOf(op)[n]; return st ? { ...op, inputs: st } : null; });
      const name = ops.find((o) => o?.inputs?.step_name)?.inputs.step_name;
      return fieldRows(ops, maxSteps > 1 ? `${name || `Équipement ${n + 1}`} — ` : "");
    }).flat().map((r) => ({ ...r, raw: r.raw.map((v) => JSON.stringify(v ?? null)) }));
    return { key: g.key, name: fiche?.name || "", rows, present: perSim.map(Boolean) };
  });
};

export const InputsTable = ({ sims, refSim, fichesByCode }) => {
  const refIdx = sims.findIndex((s) => s.id === refSim.id);
  const groups = inputsMatrix(sims, fichesByCode);
  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid="inputs-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="bg-secondary/60">
            <th className="text-left px-4 py-3 font-semibold w-64">Paramètre</th>
            {sims.map((s) => <th key={s.id} className={`text-left px-4 py-3 font-semibold truncate max-w-[220px] ${s.id === refSim.id ? "bg-genis/10" : ""}`}>{s.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => [
            <tr key={g.key} className="border-t border-border bg-secondary/30">
              <td colSpan={sims.length + 1} className="px-4 py-2 text-xs font-bold">{g.key} <span className="font-normal text-muted-foreground">— {g.name}</span></td>
            </tr>,
            ...g.rows.map((r) => (
              <tr key={`${g.key}-${r.key}`} className="border-t border-border" data-testid={`input-row-${g.key}-${r.key}`}>
                <td className="px-4 py-2 text-muted-foreground text-xs">{r.label}</td>
                {r.values.map((v, i) => {
                  const diff = i !== refIdx && r.raw[i] !== r.raw[refIdx];
                  return (
                    <td key={sims[i].id} className={`px-4 py-2 text-xs ${diff ? "bg-orange-100 text-orange-800 font-semibold" : sims[i].id === refSim.id ? "bg-genis/5" : ""}`} data-testid={`input-cell-${g.key}-${r.key}-${sims[i].id}`} data-diff={diff ? "1" : "0"}>{v}</td>
                  );
                })}
              </tr>
            )),
          ])}
        </tbody>
      </table>
    </div>
  );
};
